// The ONLY module allowed to touch XR8 — the 8th Wall camera-pipeline SLAM
// engine. Everything else sees EngineSession (src/engine/types.ts); the runtime
// choice between this and the WebXR engine lives in src/engine/engine.ts.
//
// We use the engine's OWN pipeline modules — XR8.Threejs (scene/camera/renderer,
// camera projection + pose) and XR8.FullWindowCanvas (full-screen canvas at the
// correct device-pixel-ratio + orientation). XR8.Threejs requires window.THREE;
// we hand it OUR three instance so there's a single THREE and our content objects
// are fully compatible.
import * as THREE from 'three'
import {XR8Promise} from '@8thwall/engine-binary'
import type {CameraStatus, EngineCallbacks, EngineSession} from './types'

// Must exist before XR8.Threejs.pipelineModule() runs (it throws otherwise).
;(window as unknown as {THREE: typeof THREE}).THREE = THREE

// xr.js (1.0 MB) + its SLAM chunk (5.3 MB) used to load from a <script> tag in
// index.html on EVERY page view. They are now injected on demand so devices that
// run the WebXR engine — and desktops that run neither — never fetch them.
// Injection still happens at app start for devices we know will need it
// (see preloadEngine in engine.ts), preserving the old "warm before Start AR"
// timing on iOS.
const XR8_SCRIPT_SRC = '/external/xr/xr.js'
let scriptInjected = false

/** Inject the engine binary's loader script. Idempotent; safe to call early. */
export function loadXr8Script(): void {
  if (scriptInjected || typeof document === 'undefined') return
  if (document.querySelector(`script[src="${XR8_SCRIPT_SRC}"]`)) {
    scriptInjected = true
    return
  }
  const script = document.createElement('script')
  script.src = XR8_SCRIPT_SRC
  script.async = true
  // Preloads the SLAM chunk alongside xr.js, as the old index.html tag did.
  script.setAttribute('data-preload-chunks', 'slam')
  document.head.appendChild(script)
  scriptInjected = true
}

/**
 * Boot the AR engine on a canvas. Resolves once the three.js scene exists.
 */
export async function startXr8Engine(
  canvas: HTMLCanvasElement,
  callbacks: EngineCallbacks,
): Promise<EngineSession> {
  loadXr8Script()
  const XR8 = await XR8Promise
  ;(window as unknown as {THREE: typeof THREE}).THREE = THREE

  let lastTime = performance.now()
  let litScene: THREE.Scene | null = null
  let cameraFailed = false

  const teardown = () => {
    try {
      XR8.stop()
      XR8.clearCameraPipelineModules()
    } catch (err) {
      console.warn('[engine] teardown failed', err)
    }
    // FullWindowCanvas re-parents the canvas to document.body; after the session
    // ends, take it back out so it can't linger over later views.
    if (canvas.parentElement === document.body) canvas.remove()
  }

  // Our per-frame + status hook. Added AFTER Threejs so the camera pose/projection
  // are already updated when onFrame runs; onRender (Threejs) draws afterward.
  const appModule = {
    name: 'alt-app',
    onStart: () => {
      // Set the starting camera pose (documented 8th Wall pattern). Without this
      // the camera begins at y=0 — ON the ground plane — which breaks every
      // ray-to-ground computation (reticle, tap-to-place). ~eye height; absolute
      // scale refines the true height as SLAM converges.
      const three = XR8.Threejs.xrScene()
      if (!three) return
      three.camera.position.set(0, 1.6, 0)
      XR8.XrController.updateCameraProjectionMatrix({
        origin: three.camera.position,
        facing: three.camera.quaternion,
      })
    },
    onUpdate: () => {
      const three = XR8.Threejs.xrScene()
      if (!three) return
      if (litScene !== three.scene) {
        litScene = three.scene
        addLighting(three.scene)
      }
      const now = performance.now()
      const dt = Math.min((now - lastTime) / 1000, 0.1) // clamp frame hitches
      lastTime = now
      callbacks.onFrame?.(dt, three.camera as THREE.PerspectiveCamera)
    },
    onCameraStatusChange: ({status}: {status: CameraStatus}) => {
      if (status === 'failed') cameraFailed = true
      callbacks.onCameraStatus?.(status)
    },
    onException: (error: unknown) => callbacks.onError?.(error),
  }

  XR8.clearCameraPipelineModules()
  XR8.XrController.configure({disableWorldTracking: false, scale: 'absolute'})
  XR8.addCameraPipelineModules([
    XR8.GlTextureRenderer.pipelineModule(), // draws the camera feed
    XR8.Threejs.pipelineModule(), // scene/camera/renderer + camera projection & pose
    XR8.XrController.pipelineModule(), // SLAM
    XR8.FullWindowCanvas.pipelineModule(), // full-screen canvas @ correct DPR + orientation
    appModule as never,
  ])
  XR8.run({canvas})

  // Resolve when the scene exists; fail FAST on camera denial (don't sit out the
  // timeout), and on ANY failure stop the already-running pipeline — otherwise it
  // keeps the camera + GL context alive with no owner (review finding).
  let three: XR8ThreeScene
  try {
    three = await waitFor(
      () => XR8.Threejs.xrScene(),
      () => cameraFailed,
      15000,
    )
  } catch (err) {
    teardown()
    throw err
  }
  return {
    kind: 'xr8',
    scene: three.scene as THREE.Scene,
    camera: three.camera as THREE.PerspectiveCamera,
    recenter: () => XR8.XrController.recenter(),
    stop: teardown,
  }
}

export function addLighting(scene: THREE.Scene) {
  scene.add(new THREE.HemisphereLight(0xffffff, 0x334455, 1.2))
  const dir = new THREE.DirectionalLight(0xffffff, 1.4)
  dir.position.set(2, 6, 3)
  scene.add(dir)
}

function waitFor<T>(
  get: () => T | undefined | null,
  hasFailed: () => boolean,
  timeoutMs: number,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const started = Date.now()
    const poll = () => {
      const v = get()
      if (v) return resolve(v)
      if (hasFailed()) {
        return reject(new Error('Camera access was denied or the camera is unavailable.'))
      }
      if (Date.now() - started > timeoutMs) {
        return reject(new Error('AR engine did not start (camera blocked or unsupported browser).'))
      }
      // Not rAF: browsers throttle rAF while a permission dialog is up.
      setTimeout(poll, 100)
    }
    poll()
  })
}

